import { useFormik, Form, FormikProvider } from 'formik'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReCAPTCHA from 'react-google-recaptcha'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'

import validatePhoneNumber from 'shared/utils/helpers/validatePhoneNumber'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import { httpClient, verificationRequest } from 'shared/lib'
import CustomDialog from 'shared/components/CustomDialog'
import InputField from 'shared/components/InputField'
import MuiPhone from 'shared/components/PhoneInput'

import { SmsVerificationInitialValues, SubmitOrderResult, VerifySMSCodeResult } from '../../types'
import { smsVerificationSchema } from './schema'
import { TypographyLink } from '../../styles'

interface Props {
   open: boolean
   handleClose: () => void
   token: string
}

export default function SmsVerificationModal({ open, token, handleClose }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'ORDER_REFLINK'])
   const { addNotification } = useNotificationContext()
   const [smsCodeError, setSmsCodeError] = useState('')
   const [phoneNumberEror, setPhoneNumberError] = useState(false)
   const [isLoading, setIsLoadding] = useState(false)
   const [step, setStep] = useState(0)
   const navigate = useNavigate()
   const recaptchaRef = useRef<ReCAPTCHA>(null)
   const [recaptcha, setRecaptcha] = useState('')
   const [recaptchaRequired, setRecaptchaRequired] = useState(false)

   const formik = useFormik<SmsVerificationInitialValues>({
      initialValues: {
         phoneNumber: '',
         smsCode: '',
      },
      validationSchema: smsVerificationSchema,
      onSubmit: handleSubmit,
   })

   const { values } = formik

   async function getSmsCode() {
      const { phoneNumber } = formik.values

      try {
         if (recaptchaRequired) {
            await verificationRequest.post(`sms/send?g-recaptcha-response=${recaptcha}`, {
               phoneNumber,
               category: 'VERIFICATION',
            })
         } else {
            await verificationRequest.post('sms/send', { phoneNumber, category: 'VERIFICATION' })
         }
         setSmsCodeError('')
         setStep(1)
      } catch (error) {
         if (isAxiosError<ErrorType>(error)) {
            if (error.response?.data.errors[0].code === 'errors.captcha.required') {
               setRecaptchaRequired(true)
            }
            setSmsCodeError(
               error.response?.data.errors[0].code
                  ? t(`TRANSLATION:${error.response?.data.errors[0].code}`)
                  : t('ORDER_REFLINK:smsVerification.connectionProblem')
            )
         }
      }
   }

   function validateSmsCode() {
      return verificationRequest
         .post<VerifySMSCodeResult>('sms/verify', { smsCode: values.smsCode })
         .then((res) => {
            return res.data.valid
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               if (error.response?.data?.errors[0].code === 'errors.captcha.validation-failed') {
                  setStep(0)
                  setRecaptcha('')
               }
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
            if (recaptchaRef.current) {
               recaptchaRef.current.reset()
            }
            return false
         })
   }

   async function sendOrder() {
      setIsLoadding(true)
      await httpClient
         .post<SubmitOrderResult>('order/reflink/order', {
            token,
            phoneNumber: values.phoneNumber,
         })
         .then((res) => {
            if (res.status === 200 && res.data) {
               navigate('/order-reflink-complete')
            }
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || t('ORDER_REFLINK:orderSubmitError'), 'error')
            } else {
               addNotification(t('ORDER_REFLINK:orderSubmitError'), 'error')
            }
         })
         .finally(() => setIsLoadding(false))
   }

   function handleSubmit() {
      formik.validateForm().then(async (errors) => {
         const isValidForm = Object.keys(errors).length === 0
         if (isValidForm) {
            const smsCodeIsValid = await validateSmsCode()
            if (smsCodeIsValid) {
               sendOrder()
            } else {
               formik.setFieldError('smsCode', t('ORDER_REFLINK:smsVerification.wrongSmsCode'))
            }
         }
      })
   }

   function handlePhoneNumber(value: string, country: string) {
      formik.setFieldValue('phoneNumber', value)
      const result = validatePhoneNumber(value, country)
      if (result === false) {
         setPhoneNumberError(true)
      } else {
         setPhoneNumberError(false)
      }
   }

   function handleStepChange(nextStep: number) {
      if (nextStep === 0) setStep(0)
      else if (nextStep === 1) {
         getSmsCode()
      }
   }

   function renderContent() {
      switch (step) {
         case 0:
            return (
               <>
                  <DialogTitle>{t('ORDER_REFLINK:smsVerification.phoneNumberTitle')}</DialogTitle>
                  <DialogContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                     <Typography variant="body2" sx={{ mb: 1 }} color="grayText">
                        {t('ORDER_REFLINK:smsVerification.phoneNumberText')}
                     </Typography>
                     <MuiPhone
                        variant="standard"
                        value={formik.values.phoneNumber}
                        onChange={(value, country) => handlePhoneNumber(value, country)}
                     />
                     <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {recaptchaRequired && (
                           <ReCAPTCHA
                              style={{ marginTop: 20 }}
                              sitekey="6LebPrIUAAAAAEtQ6YCkPH5RO-V1WNeAn5-dNCOc"
                              ref={recaptchaRef}
                              onChange={(value) => setRecaptcha(value || '')}
                           />
                        )}
                     </Box>
                     {smsCodeError && (
                        <Alert severity="error" sx={{ mt: 2, textAlign: 'center' }}>
                           {smsCodeError}
                        </Alert>
                     )}
                  </DialogContent>
                  <DialogActions>
                     <OutlinedButton variant="outlined" onClick={handleClose}>
                        {t('TRANSLATION:cancel')}
                     </OutlinedButton>
                     <ContainedButton
                        variant="contained"
                        type="button"
                        disabled={phoneNumberEror || (recaptchaRequired && !recaptcha)}
                        onClick={() => handleStepChange(1)}
                     >
                        {t('ORDER_REFLINK:smsVerification.smsSendButton')}
                     </ContainedButton>
                  </DialogActions>
               </>
            )
         case 1:
            return (
               <>
                  <DialogTitle>{t('ORDER_REFLINK:smsVerification.smsCodeTitle')}</DialogTitle>
                  <DialogContent>
                     <Typography variant="body1" color="grayText">
                        {t('ORDER_REFLINK:smsVerification.smsCodeText')}
                     </Typography>
                     <InputField name="smsCode" fullWidth={false} variant="standard" label="Kod SMS" />

                     <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
                        <TypographyLink onClick={getSmsCode}>
                           {t('ORDER_REFLINK:smsVerification.sendAgainButton')}
                        </TypographyLink>

                        <TypographyLink onClick={() => setStep(0)}>
                           {t('ORDER_REFLINK:smsVerification.backToSendButton')}
                        </TypographyLink>
                     </Box>
                  </DialogContent>
                  <DialogActions>
                     <ContainedButton
                        onClick={handleSubmit}
                        disabled={isLoading}
                        variant="contained"
                        sx={{
                           fontSize: 13,
                        }}
                     >
                        {t('ORDER_REFLINK:smsVerification.verifyButton')}
                     </ContainedButton>
                  </DialogActions>
               </>
            )
         default:
            return null
      }
   }
   return (
      <CustomDialog
         isLoading={!open}
         open={open}
         onClose={handleClose}
         content={
            <FormikProvider value={formik}>
               <Form>{renderContent()}</Form>
            </FormikProvider>
         }
      />
   )
}
